// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import type { CSSVariablesResolver } from '@mantine/core';
import { createTheme, MantineProvider } from '@mantine/core';
import { Notifications } from '@mantine/notifications';
import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import '@mantine/notifications/styles.css';
import { MedplumClient } from '@medplum/core';
import { MedplumProvider } from '@medplum/react';
import '@medplum/react/styles.css';
import { parsePolarisEnv } from '@polarissleep/shared';
import * as Sentry from '@sentry/react';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router';
import { App } from './App';
import { getConfig } from './config';

// Fail fast at boot if the Polaris environment is missing or malformed.
// See docs/ENVIRONMENT_ISOLATION_PLAN.md.
parsePolarisEnv(import.meta.env.VITE_POLARIS_ENV);

if (import.meta.env.VITE_PATIENT_APP_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_PATIENT_APP_SENTRY_DSN,
    environment: import.meta.env.VITE_PATIENT_APP_SENTRY_ENVIRONMENT || 'development',
    integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  });
}

const medplum = new MedplumClient({
  onUnauthenticated: () => (window.location.href = '/'),
  baseUrl: getConfig().baseUrl,
});

const theme = createTheme({
  primaryColor: 'polaris',
  colors: {
    // Generated from #640040 — shade 5 is the primary brand color.
    // Mantine uses shades 0-4 for light variants/backgrounds and 6-9 for hover/active/dark states.
    polaris: [
      '#fef0f5', // 0: lightest (backgrounds)
      '#f5d0de', // 1: light (unselected pills, hover bg)
      '#e8a1bc', // 2
      '#d6729a', // 3
      '#c44a7b', // 4
      '#640040', // 5: primary (buttons, active states)
      '#560037', // 6: hover
      '#47002e', // 7: active/pressed
      '#380024', // 8
      '#2a001b', // 9: darkest
    ],
  },
  headings: {
    sizes: {
      h1: {
        fontSize: '1.125rem',
        fontWeight: '500',
        lineHeight: '2.0',
      },
    },
  },
  fontSizes: {
    xs: '0.6875rem',
    sm: '0.875rem',
    md: '0.875rem',
    lg: '1.0rem',
    xl: '1.125rem',
  },
});

const cssVariablesResolver: CSSVariablesResolver = () => ({
  variables: {
    '--polaris-light-purple': '#FEF4FF',
  },
  light: {
    '--mantine-color-body': '#ffffff',
    '--mantine-primary-color-light-hover': 'transparent',
  },
  dark: {},
});

const container = document.getElementById('root') as HTMLDivElement;
const root = createRoot(container);
root.render(
  <StrictMode>
    <BrowserRouter>
      <MedplumProvider medplum={medplum}>
        <MantineProvider theme={theme} cssVariablesResolver={cssVariablesResolver}>
          <Notifications />
          <App />
        </MantineProvider>
      </MedplumProvider>
    </BrowserRouter>
  </StrictMode>
);
